import { useMemo } from 'react';
import { useDialer } from '@/contexts/DialerContext';
import { useActiveCallActor } from '@/state/useActiveCallActor';
import { useIncomingCallSession } from '@/hooks/useIncomingCallSession';
import { useNow } from '@/hooks/useNow';

/**
 * « Qui est en ligne ? » pour l'affichage : la barre d'appel (FloatingCallBar)
 * et la pastille du rail (DES-2) lisent la même réponse. Lecture seule : les
 * trois sources sont des instances partagées (DialerUIContext via useDialer,
 * le pool d'appels, le contexte de session entrante), rien n'est recréé.
 *
 * Priorité (docs/refactor/sources-of-truth.md) : le contact du contexte,
 * toujours enrichi ; sinon celui de la machine d'appel ; sinon l'appel
 * entrant décroché, complété par la liste quand le numéro y est. Exception
 * (CALL-PILL-1) : quand l'appel précédent attend son issue, l'entrant
 * décroché est l'appel audible et passe devant.
 */
/** A line untouched for 10 minutes counts as stuck (ENG-3), not as ringing. */
const STALE_LINE_MS = 10 * 60 * 1000;
/** Stable fallback, so the memo below does not rerun on every render. */
const EMPTY_QUEUE: Array<{ prospect_phone?: string | null; prospect_company?: string | null }> = [];

export interface LiveCallContact {
  prospect_id?: string;
  prospect_name?: string;
  name?: string;
  prospect_phone?: string;
  phone?: string;
  prospect_company?: string;
  company?: string;
  callId?: string | null;
  callStartedAt?: number | string | Date | null;
  [key: string]: unknown;
}

export function useLiveCallContact() {
  const dialer = useDialer() as {
    connectedContact?: LiveCallContact | null;
    prospectQueue?: Array<{ prospect_phone?: string | null; prospect_company?: string | null }>;
    activeCalls?: Array<{ updated_date?: string | null; created_date?: string | null }>;
    dialerRunState?: string;
  };
  const ctxConnectedContact = dialer.connectedContact ?? null;
  const prospectQueue = dialer.prospectQueue ?? EMPTY_QUEUE;
  const machineView = useActiveCallActor();
  const inbound = useIncomingCallSession();
  // Memoised: FloatingCallBar and the rail pill re-render every second
  // during a call; the queue scan runs only when its inputs change.
  const inboundProspect = inbound.isLive ? inbound.prospect : null;
  const inboundContact: LiveCallContact | null = useMemo(() => {
    if (!inboundProspect) return null;
    const known = prospectQueue.find((p) => p.prospect_phone && p.prospect_phone === inboundProspect.phone);
    return {
      prospect_name: inboundProspect.name,
      prospect_phone: inboundProspect.phone,
      prospect_company: inboundProspect.company || known?.prospect_company || '',
      callStartedAt: inbound.startedAt ?? undefined,
    };
  }, [inboundProspect, prospectQueue, inbound.startedAt]);
  const machineContact = (machineView.connectedContact as LiveCallContact | null) ?? null;
  // After hang-up the call machine stays in `qualified` with its contact
  // until the outcome is saved: the contact is still there, the call is not.
  const previousAwaitsOutcome = machineView.callEnded === true;
  // An inbound call answered while the previous call waits for its outcome
  // is the one on the line (CALL-PILL-1): it is shown, and hanging up ends
  // it alone; the pending qualification stays for after.
  const inboundOverPending = !!inboundContact && previousAwaitsOutcome;
  const isInboundLive = !!inboundContact && (inboundOverPending || (!ctxConnectedContact && !machineContact));
  const ctxPhone = ctxConnectedContact?.prospect_phone ?? ctxConnectedContact?.phone;
  const ctxIsInbound = !!ctxPhone && ctxPhone === inboundContact?.prospect_phone;
  const connectedContact = inboundOverPending
    ? (ctxIsInbound ? ctxConnectedContact : inboundContact)
    : (ctxConnectedContact ?? machineContact ?? inboundContact);
  const callEnded = !isInboundLive && previousAwaitsOutcome;
  const isLive = (!!connectedContact && !callEnded) || !!inboundContact;
  // Something on screen would be lost by a reload: a live call, or an ended
  // one still waiting for its qualification.
  const hasCallOnScreen = !!connectedContact;
  // A full reload (organisation switch, ENG-9) would also drop lines still
  // ringing in a running session, or an inbound call not yet answered: a
  // prospect who picks up a second later reaches nobody (ORG-SWITCH-1).
  // Only lines active in the last 10 minutes count: a ParallelCall stuck in a
  // live status (ENG-3) must not forbid the switch for ever; a long connected
  // call is covered by isLive.
  // Re-read every 30 s: a line goes stale with time alone, no data changes.
  const now = useNow(30_000);
  const ringing = (dialer.activeCalls ?? []).some((c) => {
    const at = Date.parse(c.updated_date ?? c.created_date ?? '');
    // An undated line is not proof of a live one: it never blocks.
    return !Number.isNaN(at) && now - at < STALE_LINE_MS;
  });
  const blocksReload = hasCallOnScreen || isLive || inbound.isRinging === true
    || dialer.dialerRunState === 'running' || ringing;
  return { connectedContact, isInboundLive, inbound, callEnded, isLive, hasCallOnScreen, blocksReload };
}
